/**
 * `AmbianceSM` : une ambiance par zone de santé mentale (⟦A-76⟧, étendu ⟦A-160⟧ · C35).
 *
 * SPEC-UI §7.5-7.6 : chaque zone `zoneSM` a sa musique d'ambiance, en boucle, à volume bas. La
 * DÉCISION (quel morceau, quelle vitesse) est déléguée à `pisteAmbianceSM` (`audio/ambiances.ts`,
 * pur et testé) ; ce composant ne fait qu'obéir. PLAN §4.10 voulait cette ambiance depuis le
 * début ; ⟦A-76⟧ n'en avait livré qu'une (zone rouge) ; ⟦A-160⟧ (retour de Matt du 18/09/2026)
 * l'étend aux quatre zones : vert et fatigue jouent désormais, crise reste silencieuse.
 *
 * ## Pourquoi une pompe à intervalle, et pas un simple effet sur la zone
 *
 * Le lecteur de musique est **un seul canal**, et d'autres écrans le prennent légitimement :
 * la piste du Montage (⟦A-76⟧), la fanfare de la cérémonie, le thème du mémorial (⟦A-71⟧).
 * Un effet qui ne réagirait qu'au changement de zone raterait le moment où l'un d'eux rend le
 * canal : l'ambiance resterait éteinte alors que la zone en voudrait une. La pompe (1 s, ce
 * n'est pas du gameplay) applique une règle simple et sans mémoire, à chaque tic :
 *
 * - zone avec ambiance et canal **libre** → elle démarre, en fondu depuis le silence ;
 * - zone avec ambiance et notre PROPRE ambiance déjà en cours, mais **une autre piste** (la
 *   zone a changé) → fondu enchaîné vers la nouvelle, jamais une coupure sèche (consigne SFX du
 *   13/09) ;
 * - zone avec ambiance et canal **occupé par un autre morceau** → on ne vole jamais le canal ;
 * - zone `crise` (pas d'ambiance) et la nôtre joue → elle s'éteint en fondu.
 *
 * Elle rattrape au passage le cas du son réactivé en cours de zone sonore (`jouer` est un no-op
 * sous `mute` : au tic suivant, l'ambiance revient).
 *
 * ⚠️ **Pourquoi `zoneRef` et pas `zone` en dépendance d'effet** : avec `zone` en dépendance,
 * React démonterait puis remonterait l'effet à CHAQUE changement de zone, et son nettoyage
 * coupe la musique SANS fondu (`arreter()` par défaut), avant même que le nouvel effet ait pu
 * lancer le fondu enchaîné. La pompe reste donc montée une seule fois ; `zoneRef.current` lui
 * donne la zone fraîche à chaque tic, et c'est **elle**, à l'intérieur, qui gère le fondu.
 *
 * ## L'état neutre, comme toujours
 *
 * Composant sans rendu, aucun barème ne le lit, rien ne l'attend : couper cette ambiance ne
 * change ni un score, ni une durée, ni un tirage (⟦A-22⟧ : la règle de relecture est en tête
 * de `audio/beatmap.ts`). La zone `crise` (SM 0) ne joue rien : c'est l'écran qui parle, pas
 * la bande-son.
 */

import { useEffect, useRef } from "react";

import { useMusique } from "../components/SoundProvider";
import { zoneSM } from "../game/format";
import { useRunStore } from "../stores/runStore";

import { IDS_AMBIANCE_SM, VOLUME_AMBIANCE_SM, pisteAmbianceSM } from "./ambiances";

// Réexportés pour compatibilité : la décision vit maintenant dans `ambiances.ts` (pur, testé).
export { RALENTI_ZONE_FATIGUE, RALENTI_ZONE_ROUGE } from "./ambiances";

/** Le fondu ⟦A-160⟧ entre deux zones : et aussi le fondu de départ (silence → ambiance) et
 *  d'arrêt (ambiance → silence, zone crise). Une seule durée : pas de raison qu'entrer aille
 *  plus vite que sortir. « Environ 1 s », consigne de Matt : jamais en coupe sèche. */
const FONDU_AMBIANCE_MS = 1000;

export function AmbianceSM() {
  const mental = useRunStore((s) => s.run?.state.mental ?? 100);
  const musique = useMusique();
  const zone = zoneSM(mental);
  // Valeur fraîche pour la pompe ci-dessous, SANS remonter l'effet à chaque tour de zone
  // (voir l'avertissement de l'en-tête) : une simple affectation pendant le rendu, lue plus
  // tard dans un callback : le usage documenté par React pour ce cas précis.
  const zoneRef = useRef(zone);
  zoneRef.current = zone;

  useEffect(() => {
    if (!musique) return;
    const options = (playbackRate: number) => ({
      boucle: true,
      volume: VOLUME_AMBIANCE_SM,
      playbackRate,
      fondueMs: FONDU_AMBIANCE_MS,
    });
    const pomper = () => {
      const cible = pisteAmbianceSM(zoneRef.current);
      const encours = musique.idEnCours();

      if (cible === null) {
        // Zone crise : silence. On ne coupe QUE notre propre ambiance (canal unique, ⟦A-76⟧).
        if (encours !== null && IDS_AMBIANCE_SM.has(encours)) {
          musique.arreter({ fondueMs: FONDU_AMBIANCE_MS });
        }
        return;
      }

      if (encours === null) {
        musique.jouer(cible.id, options(cible.playbackRate));
      } else if (IDS_AMBIANCE_SM.has(encours) && encours !== cible.id) {
        // La zone a changé PENDANT que notre ambiance jouait : `arreter()` referme l'ancien
        // morceau sur son propre nœud de gain (il continue de jouer, en fondu, indépendamment
        // de ce que `jouer()` s'apprête à démarrer) ; `jouer()` ouvre un nœud NEUF qui monte en
        // fondu par-dessus. Les deux se chevauchent le temps du fondu : c'est le fondu enchaîné,
        // jamais une coupure sèche.
        musique.arreter({ fondueMs: FONDU_AMBIANCE_MS });
        musique.jouer(cible.id, options(cible.playbackRate));
      }
      // Sinon : `encours` est soit déjà la bonne piste, soit un morceau d'un autre écran (piste
      // du Montage, fanfare, mémorial) : le canal unique dit qu'on ne le vole jamais.
    };
    pomper();
    const minuterie = window.setInterval(pomper, 1000);
    return () => {
      window.clearInterval(minuterie);
      // On ne coupe en partant QUE notre ambiance : ce composant vit à la racine, il ne se
      // démonte qu'avec l'app : mais un démontage ne doit jamais couper le morceau d'un autre
      // écran. Pas de fondu ici : un écran qu'on quitte (l'app se ferme) ne traîne pas de queue.
      const encours = musique.idEnCours();
      if (encours !== null && IDS_AMBIANCE_SM.has(encours)) musique.arreter();
    };
  }, [musique]);

  return null;
}
